import React, { forwardRef, useCallback, useMemo } from 'react';
import clsx from 'classnames';
import { SurveyTheme } from '@sm/webassets/dist/components/SurveyTheme/types';
import { createSurveyStyles } from '@sm/webassets';
import { useSurveyFormatContext, useActiveViewContext } from '../useSurveyFormatContext';
import { SurveyFormatViewProps } from '../types';
import { findFirstFocusable, isChildFunction } from '../utils';
import useFormatView from './useFormatView';

const useStyles = createSurveyStyles<'shield', { active: boolean; noShield: boolean; autoPadding: boolean }>(
  (theme: SurveyTheme) => ({
    shield: {
      opacity: ({ active, noShield }) => (!!noShield || active ? 1 : 0.5),
      paddingBottom: ({ autoPadding }) => (autoPadding ? '80px' : 0),
      [`@media (max-width: ${theme.breakpoints?.xs.max})`]: {
        paddingBottom: ({ autoPadding }) => (autoPadding ? '270px' : 0),
      },
    },
  })
);

export function OQAATFormatView(
  { name, noShield = false, autoPadding = true, className, children, ...props }: SurveyFormatViewProps,
  forwardedRef?: React.Ref<HTMLDivElement>
): React.ReactElement {
  const formatContext = useSurveyFormatContext();
  const { active, setActive, setNextActive } = useActiveViewContext();

  const { isActive, ref } = useFormatView(name, formatContext, active, forwardedRef);

  const { shield } = useStyles({ active: isActive, noShield, autoPadding });

  const handleShieldClick = useCallback(() => {
    if (!isActive && ref?.current) {
      const firstFocusable = findFirstFocusable(ref.current);
      firstFocusable?.focus();
    }
  }, [isActive, ref]);

  const handleFocus = useCallback(
    (e: React.FocusEvent) => {
      const ele = ref?.current as HTMLElement;
      if (!isActive && ele !== e.target && ele.contains(e.target)) {
        setActive(name);
      }
    },
    [name, ref, isActive, setActive]
  );

  // only re-render children if their active state changes
  const content = useMemo(() => {
    if (isChildFunction(children)) {
      return children(isActive, setNextActive);
    }
    if (children) {
      return React.cloneElement(children, { active: isActive, onSubmit: setNextActive });
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, children]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onFocus={handleFocus}
      onClick={handleShieldClick}
      // `offset-target-container` is needed for apps/respweb/views/spa-survey.hbs to calculate initial `marginTop`
      className={clsx('offset-target-container', shield, className)}
      {...props}
      ref={ref}
    >
      {content}
    </div>
  );
}

export default forwardRef<HTMLDivElement, SurveyFormatViewProps>(OQAATFormatView);
